'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { webPagesClient } from '@terra/shared-rest-clients-web-pages';
import type { WebpageRelatedEndpointSchema } from '@terra/shared-contracts-web-pages';
import { WEBPAGE_RELATED_ENDPOINTS_DEFAULT_LIMIT } from '@terra/shared-contracts-web-pages';
import type { z } from 'zod';

export const WEBPAGE_ENDPOINTS_QUERY_KEY = 'webpage-endpoints';

export type WebpageRelatedEndpoint = z.infer<
  typeof WebpageRelatedEndpointSchema
>;

type UseWebpageEndpointsParams = {
  webpageId: string | undefined;
  enabled?: boolean;
};

type WebpageEndpointsResult = {
  endpoints: WebpageRelatedEndpoint[];
  totalCount: number;
  skip: number;
};

const fetchWebpageEndpoints = async ({
  skip,
  webpageId,
}: {
  skip: number;
  webpageId: string;
}): Promise<WebpageEndpointsResult> => {
  const response = await webPagesClient.getWebpageEndpoints({
    params: { id: webpageId },
    query: {
      skip,
      limit: WEBPAGE_RELATED_ENDPOINTS_DEFAULT_LIMIT,
    },
  });

  if (response.status === 200) {
    return {
      endpoints: response.body.endpoints,
      totalCount: response.body.totalCount,
      skip,
    };
  }

  throw new Error('Failed to load webpage endpoints');
};

export const useWebpageEndpoints = ({
  webpageId,
  enabled = true,
}: UseWebpageEndpointsParams) => {
  const queryResult = useInfiniteQuery({
    queryKey: [
      WEBPAGE_ENDPOINTS_QUERY_KEY,
      webpageId,
      WEBPAGE_RELATED_ENDPOINTS_DEFAULT_LIMIT,
    ],
    queryFn: ({ pageParam }) => {
      if (!webpageId) {
        return Promise.resolve({
          endpoints: [],
          totalCount: 0,
          skip: pageParam,
        });
      }
      return fetchWebpageEndpoints({
        webpageId,
        skip: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextSkip = lastPage.skip + lastPage.endpoints.length;
      return nextSkip < lastPage.totalCount ? nextSkip : undefined;
    },
    enabled: enabled && Boolean(webpageId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  return {
    endpoints: queryResult.data?.pages.flatMap((page) => page.endpoints) ?? [],
    totalCount: queryResult.data?.pages[0]?.totalCount ?? 0,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};
